// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import updateSearchFields from "@store/Framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import { StoreGetCartsApiParameter } from "@store/Services/CartService";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import debounce from "lodash/debounce";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
});

const mapDispatchToProps = {
    updateSearchFields,
    getAutocompleteModel,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListSearchFieldProductErpNumberStyles {
    wrapper?: InjectableCss;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    autocompleteWrapper?: InjectableCss;
    clearIcon?: IconPresentationProps;
}

export const savedOrderListSearchFieldProductErpNumberStyles: SavedOrderListSearchFieldProductErpNumberStyles = {
    wrapper: {
        css: css`
            padding: 10px;
            width: 100%;
            position: relative;
        `,
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: ${pxToRem(50)};
            height: ${pxToRem(50)};
            margin-right: 10px;
        `,
    },
    productImage: {
        width: pxToRem(50),
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        ellipsis: true,
        css: css`
            margin-top: ${pxToRem(5)};
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
    autocompleteWrapper: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    clearIcon: {
        src: X,
        css: css`
            position: absolute;
            height: 48px;
            width: 48px;
            top: 40px;
            right: 40px;
            justify-content: center;
        `,
    },
    searchDynamicDropdown: {
        cssOverrides: {
            inputSelect: css`
                &.hasIcon {
                    padding-right: ${pxToRem(70)};
                }
            `,
        },
    },
};
const styles = savedOrderListSearchFieldProductErpNumberStyles;

const SavedOrderListSearchFieldProductErpNumber = ({
    getCartsApiParameter,
    updateSearchFields,
    getAutocompleteModel,
}: Props) => {
    const productErpNumber = (getCartsApiParameter as StoreGetCartsApiParameter).productErpNumber
        ? (getCartsApiParameter as StoreGetCartsApiParameter).productErpNumber
        : undefined;

    const [options, setOptions] = React.useState<OptionObject[]>([]);
    const onSelectionChangeHandler = (value?: string) => {
        updateSearchFields({ productErpNumber: value });
    };

    const clearButtonHandler = () => {
        updateSearchFields({ productErpNumber: "" });
    };

    React.useEffect(() => {
        if (productErpNumber) {
            debouncedSearchProducts(productErpNumber);
        }
    }, []);

    const debouncedSearchProducts = debounce((query: string) => {
        getAutocompleteModel({
            query,
            onSuccess: result => {
                if (!result?.products) {
                    return;
                }

                const newOptions = result.products.map(product => ({
                    optionText: `${product.erpNumber}  ${product.title}`,
                    optionValue: product.erpNumber || undefined,
                    rowChildren: (
                        <StyledWrapper {...styles.optionWrapper}>
                            <StyledWrapper {...styles.imageWrapper}>
                                <LazyImage {...styles.productImage} src={product.image} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoWrapper}>
                                <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                                <Typography {...styles.autocompleteWrapper}>
                                    {product.isNameCustomerOverride && (
                                        <>
                                            {translate("CLIENT PART #")}: &nbsp;
                                            <Typography
                                                {...styles.autocompleteErpText}
                                                data-test-selector="autocompleteProducts_PartNumber"
                                            >
                                                {product.name}
                                            </Typography>
                                        </>
                                    )}
                                    {!product.isNameCustomerOverride && (
                                        <>
                                            {translate("BRS SKU #")}: &nbsp;
                                            <Typography
                                                {...styles.autocompleteErpText}
                                                data-test-selector="autocompleteProducts_PartNumber"
                                            >
                                                {product.erpNumber}
                                            </Typography>
                                        </>
                                    )}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    ),
                }));
                setOptions(newOptions);
            },
            onComplete(resultProps) {
                if (resultProps?.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps?.apiResult);
                }
            },
        });
    }, 300);

    const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        debouncedSearchProducts(event.target.value);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <DynamicDropdown
                label={translate("Product")}
                placeholder={translate("Enter keyword or item #")}
                options={options}
                onSelectionChange={onSelectionChangeHandler}
                onInputChange={onInputChanged}
                filterOption={() => true}
                selected={productErpNumber}
                {...styles.searchDynamicDropdown}
            />
            {productErpNumber && <Icon {...styles.clearIcon} onClick={clearButtonHandler} />}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SavedOrderListSearchFieldProductErpNumber),
    definition: {
        group: "Saved Order List",
        displayName: "Product ERP Number",
        allowedContexts: ["SavedOrderListPage"],
    },
};

export default widgetModule;
